import { useCallback, useMemo } from "react";

import { useAccountSettings } from "@/contexts/AccountSettingsContext";
import type { Account } from "@/hooks/useAccount";

/** Where the pins live in the account's settings: they follow the person across devices. */
export const PINNED_DASHBOARDS_SETTING = "pinned_dashboards";
/** Enough for a personal shortlist; beyond it the oldest pin makes room. */
export const MAX_PINNED_DASHBOARDS = 8;

export function pinnedDashboardIds(settings: Account["settings"] | undefined): string[] {
  const value = settings?.[PINNED_DASHBOARDS_SETTING];
  return Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === "string")
    : [];
}

/** Pins in their new order: a new pin goes first, and the list keeps its size limit. */
export function togglePinnedId(ids: readonly string[], id: string): string[] {
  return ids.includes(id)
    ? ids.filter((entry) => entry !== id)
    : [id, ...ids].slice(0, MAX_PINNED_DASHBOARDS);
}

/**
 * The dashboards a person pinned, most recent pin first. Stored in their account
 * settings and updated optimistically, so a pin shows at once everywhere (list,
 * navigation, Home) and is restored if the save fails.
 */
export function usePinnedDashboards() {
  const { settings, updateSettings, available, isError } = useAccountSettings();
  const ids = useMemo(() => pinnedDashboardIds(settings), [settings]);

  const toggle = useCallback(
    (id: string) => {
      updateSettings((current) => ({
        ...current,
        [PINNED_DASHBOARDS_SETTING]: togglePinnedId(pinnedDashboardIds(current), id),
      }));
    },
    [updateSettings],
  );

  return {
    ids,
    isPinned: useCallback((id: string) => ids.includes(id), [ids]),
    toggle,
    /** Pinning needs the account to be loaded: its settings are where pins are kept. */
    available,
    isError,
  };
}
